import React, { FC } from 'react';
import { CleanUnorderedList, ListItem } from 'src/common/components/List';
import { EquipmentItem } from 'src/app/common/models/product';
import styled, { css } from 'styled-components';
import { CategoryEquipmentListTitle } from 'src/app/product/components/ProductPageSectionEquipment/CategoryEquipmentListTitle';
import { isNotNilEmpty } from 'src/lib/isNilEmpty';
import { StyleProps } from '@summer/react-kit';

const StyledCleanUnorderedList = styled(CleanUnorderedList)`
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
`;

const ItemCode = styled.span`
  font-weight: bold;
  margin-right: 0.25rem;
`;

const EquipmentListItem = styled<
  FC<StyleProps & { overflowEllipsis?: boolean }>
>(ListItem)`
  margin-top: 0.3rem;
  margin-bottom: 0.3rem;

  ${({ overflowEllipsis }) =>
    overflowEllipsis &&
    css`
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`;

const Link = styled.a`
  text-decoration: underline;
  cursor: pointer;
  color: rgb(224, 227, 232);

  &:visited {
    color: rgb(224, 227, 232);
  }

  &:hover {
    opacity: 0.85;
  }
`;

const URL_REGEX = /((https?:\/\/|www\.)[^\s<>()\[\]{}"'`]+)(?=[\s<>()\[\]{}"'`]|$)/gi;

function normalizeUrl(url: string) {
  return url.startsWith('http://') || url.startsWith('https://')
    ? url
    : `https://${url}`;
}

function renderTextWithLinks(text: string) {
  if (!text) return null;

  const parts: Array<{ type: 'text' | 'link'; value: string }> = [];
  let lastIndex = 0;

  const matches = Array.from(text.matchAll(URL_REGEX));

  if (matches.length === 0) return text;

  for (const m of matches) {
    const rawUrl = m[0];
    const start = m.index ?? 0;
    const end = start + rawUrl.length;

    if (start > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, start) });
    }

    parts.push({ type: 'link', value: rawUrl });
    lastIndex = end;
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return (
    <>
      {parts.map((p, idx) =>
        p.type === 'link' ? (
          <Link
            key={`link-${idx}`}
            href={normalizeUrl(p.value)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {p.value}
          </Link>
        ) : (
          <React.Fragment key={`text-${idx}`}>{p.value}</React.Fragment>
        )
      )}
    </>
  );
}

export interface CategoryEquipmentListProps {
  label?: string;
  flatEquipmentItems?: string[];
  equipmentItems?: EquipmentItem[];
  displayCodes?: boolean;
  overflowEllipsis?: boolean;
}

export const CategoryEquipmentList: FC<
  CategoryEquipmentListProps & StyleProps
> = ({
  label,
  flatEquipmentItems,
  equipmentItems,
  displayCodes = false,
  overflowEllipsis = false,
}) => {
  return (
    <>
      {isNotNilEmpty(equipmentItems) && (
        <div>
          {label && (
            <CategoryEquipmentListTitle>{label}</CategoryEquipmentListTitle>
          )}
          <StyledCleanUnorderedList>
            {equipmentItems.map((item) =>
              displayCodes ? (
                <EquipmentListItem
                  key={item.id}
                  overflowEllipsis={overflowEllipsis}
                >
                  {item.code && <ItemCode>{item.code}</ItemCode>}
                  <span>{renderTextWithLinks(item.label)}</span>
                </EquipmentListItem>
              ) : (
                <EquipmentListItem
                  key={item.id}
                  overflowEllipsis={overflowEllipsis}
                >
                  <div>{renderTextWithLinks(item.label)}</div>
                </EquipmentListItem>
              )
            )}
          </StyledCleanUnorderedList>
        </div>
      )}

      {isNotNilEmpty(flatEquipmentItems) && (
        <div>
          {label && (
            <CategoryEquipmentListTitle>{label}</CategoryEquipmentListTitle>
          )}
          <StyledCleanUnorderedList>
            {flatEquipmentItems.map((item) => (
              <EquipmentListItem key={item} overflowEllipsis={overflowEllipsis}>
                <span>{renderTextWithLinks(item)}</span>
              </EquipmentListItem>
            ))}
          </StyledCleanUnorderedList>
        </div>
      )}
    </>
  );
};
